import type { SilencingMetadata } from '@/api/hooks/useIntegrationLogs'
import { friendlySagaName } from '@/constants/sagaTypes'
import type { components } from '@/types/api/v1'

type IntegrationCheckpoints = components['schemas']['IntegrationCheckpoints']

/**
 * Checkpoint field name on OrderIntegrationCheckpoints (shared_models/
 * logs.py) -> the saga that drives it. `completed` is reached by a status
 * update rather than a saga of its own, so it has no name to look up.
 */
const CHECKPOINT_SAGAS: Record<string, string | null> = {
  created: 'create_order',
  status_updates: 'progress_status',
  bols_saved: 'progress_bol',
  bol_images_saved: 'progress_bol_image',
  drops_saved: 'progress_drop',
  completed: null,
  structure_updates: 'update_order',
  canceled: 'cancel_order',
  reset: 'reset_order',
  invoices_sent: 'add_invoice',
}

const FALLBACK_LABELS: Record<string, string> = {
  completed: 'Completed',
}

/**
 * How a checkpoint is labelled everywhere it's listed -- the friendly name of
 * the saga behind it, so the checkpoint dots, the Filters popover and the
 * grids' Event column all read the same.
 */
export function checkpointLabel(checkpoint: string): string {
  const saga = CHECKPOINT_SAGAS[checkpoint]
  return (
    (saga ? friendlySagaName(saga) : null) ??
    FALLBACK_LABELS[checkpoint] ??
    checkpoint
  )
}

function entries(checkpoints: string[]): [string, string][] {
  return checkpoints.map((checkpoint) => [
    checkpoint,
    checkpointLabel(checkpoint),
  ])
}

/** Checkpoints on the happy path of every order, in the order product wants
 * them drawn. */
export const CORE_ORDER_CHECKPOINTS = entries([
  'created',
  'status_updates',
  'bols_saved',
  'bol_images_saved',
  'drops_saved',
  'completed',
])

/** Checkpoints only some orders ever reach -- drawn after a divider. */
export const OPTIONAL_ORDER_CHECKPOINTS = entries([
  'structure_updates',
  'canceled',
  'reset',
  'invoices_sent',
])

/**
 * Whether every checkpoint that failed on this group has been silenced, so
 * the group has nothing left to raise.
 *
 * Not the same as `fully_silenced`, which asks whether *all* checkpoints are
 * silenced regardless of how they fared -- an order whose one failing
 * checkpoint is silenced still wants hiding even though its other, healthy
 * checkpoints aren't. Silencing is stored per saga and a saga can back more
 * than one checkpoint, hence the metadata lookup.
 *
 * Returns false while the metadata is still loading, so a slow fetch shows
 * too many rows rather than hiding failures the user needs to see.
 */
export function failuresAllSilenced(
  checkpoints: IntegrationCheckpoints,
  metadata: Record<string, SilencingMetadata> | undefined,
): boolean {
  if (!metadata) return false
  if (checkpoints.type !== 'OrderIntegrationCheckpoints') return false

  const silenced = new Set<string>(checkpoints.silenced_sagas)
  const failedKeys = Object.keys(metadata).filter(
    (key) =>
      checkpoints[key as keyof typeof checkpoints] === ('failed' as const),
  )

  if (failedKeys.length === 0) return false
  // An unmapped checkpoint has no saga to silence, so it still counts as live.
  return failedKeys.every((key) => {
    const saga = metadata[key]?.saga
    return saga != null && silenced.has(saga)
  })
}
